import axios from "axios";

import { api } from "@/services/api.service";

export type PartnerDirectoryScrapeTaskStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";
export type PartnerDirectoryCrawlRunStatus = "queued" | "running" | "paused" | "succeeded" | "failed" | "cancelled";
export type PartnerDirectoryCrawlPageStatus = "pending" | "running" | "succeeded" | "failed" | "skipped";
export type PartnerDirectoryDiscoveredProfileStatus =
  | "discovered"
  | "duplicate_in_run"
  | "already_indexed"
  | "scrape_queued"
  | "scraped"
  | "scrape_failed"
  | "skipped";
export type PartnerDirectoryRecordStatusFilter = "all" | "screening" | "new" | "needs_review" | "approved" | "rejected" | "imported";
export type PartnerDirectoryRecordSort = "newest" | "recently_edited" | "score" | "rating" | "reviews";
export type PartnerDirectoryBulkRecordStatus = "new" | "needs_review" | "approved" | "rejected";

export type PartnerDirectoryScrapeTask = {
  id: string;
  input: string;
  sourceSlug: string | null;
  sourceUrl: string;
  status: PartnerDirectoryScrapeTaskStatus;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  errorDetails: Record<string, unknown> | null;
  extractionWarnings: unknown[] | null;
  resultRecordId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PartnerDirectoryRecord = {
  id: string;
  sourceSlug: string | null;
  sourceUrl: string;
  name: string | null;
  websiteUrl: string | null;
  primaryLocation: string | null;
  country: string | null;
  startingPriceCents: number | null;
  rating: number | null;
  reviewCount: number | null;
  services: string[];
  status: string;
  score: number | null;
  rawPayload: Record<string, unknown>;
  normalizedPayload: Record<string, unknown>;
  mappedPayload: Record<string, unknown> | null;
  mappedName: string | null;
  avatarUrl: string | null;
  avatarMediaId: string | null;
  avatarMediaUrl: string | null;
  platformExpertId: number | null;
  platformExpertSlug: string | null;
  platformClaimed: boolean;
  importError: string | null;
  importExpertCreated: boolean;
  lastImportedAt: string | null;
  noteCount?: number;
  createdAt: string;
  updatedAt: string;
};

export type PartnerDirectoryExpertSocialLinks = {
  website: string | null;
  shopify_directory_link: string | null;
  linkedin_url: string | null;
  x_url: string | null;
};

/**
 * Shape shared by the server-derived scrape defaults (og baseline) and the
 * effective mapped payload edited on the scraper page. The record's stored
 * mappedPayload is a sparse subset of these keys: only admin overrides.
 */
export type PartnerDirectoryMappedExpertPayload = {
  expert_id: number | null;
  expert_type: "agency" | "freelancer";
  agency_name: string | null;
  first_name: string | null;
  last_name: string | null;
  bio: string | null;
  country: string | null;
  starting_price: number | null;
  timezone: string | null;
  phone_no: string | null;
  languages: string[];
  social_links: PartnerDirectoryExpertSocialLinks;
  subcategory_slugs: string[];
};

export type PartnerDirectoryExpertProfileStatus = "draft" | "published";

export type PartnerDirectoryImportJobStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";

export type PartnerDirectoryImportJobRecordOutcome = "imported" | "failed" | "skipped";

/** Immutable snapshot of what a job did to one record, joined with the record's current state. */
export type PartnerDirectoryImportJobRecord = {
  id: string;
  jobId: string;
  recordId: string;
  name: string | null;
  outcome: PartnerDirectoryImportJobRecordOutcome;
  errorMessage: string | null;
  platformExpertId: number | null;
  platformExpertSlug: string | null;
  createdAt: string;
  record: PartnerDirectoryRecord | null;
};

export type PartnerDirectoryImportJobRevertOutcome = "deleted" | "already_deleted" | "skipped_claimed" | "fields_reverted" | "nothing_to_revert";

/** Laravel revert response data, persisted on the job as revertSummary. */
export type PartnerDirectoryImportJobRevertResult = {
  job_id: string;
  totals: {
    experts_deleted: number;
    experts_skipped: number;
    actions_reverted: number;
    actions_skipped: number;
  };
  experts: {
    expert_id: number;
    partner_id: string;
    outcome: PartnerDirectoryImportJobRevertOutcome;
    actions_reverted: number;
    actions_skipped: { id: number; action: string; field: string | null; reason: string }[];
  }[];
};

export type PartnerDirectoryImportJob = {
  id: string;
  status: PartnerDirectoryImportJobStatus;
  targetProfileStatus: PartnerDirectoryExpertProfileStatus;
  recordsTotal: number;
  recordsSucceeded: number;
  recordsFailed: number;
  recordsSkipped: number;
  recordIds: string[] | null;
  requestedByAdminId: string | null;
  requestedByAdminEmail: string | null;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  revertedAt: string | null;
  revertSummary: PartnerDirectoryImportJobRevertResult | null;
  createdAt: string;
  updatedAt: string;
};

export type PartnerDirectoryRecordNote = {
  id: string;
  recordId: string;
  authorAdminId: string | null;
  authorAdminEmail: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
};

export type PartnerDirectoryGlobalMappingKind = "service" | "location" | "language" | "industry";

export type PartnerDirectoryGlobalMapping = {
  id: string;
  kind: PartnerDirectoryGlobalMappingKind;
  sourceValue: string;
  targetValue: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PartnerDirectoryCrawlOptions = {
  maxPages: number | null;
  maxProfiles: number | null;
  queueLimit: number | null;
  enqueueScrapeTasks: boolean;
  scrapeForce: boolean;
};

export type PartnerDirectoryCrawlRun = {
  id: string;
  status: PartnerDirectoryCrawlRunStatus;
  seedUrl: string;
  mode: string;
  options: PartnerDirectoryCrawlOptions;
  requestedByAdminId: string | null;
  requestedByAdminEmail: string | null;
  pagesDiscovered: number;
  pagesSucceeded: number;
  pagesFailed: number;
  profilesDiscovered: number;
  profilesNew: number;
  profilesDuplicate: number;
  startedAt: string | null;
  pausedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  errorDetails: Record<string, unknown> | null;
  resetOfRunId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PartnerDirectoryCrawlPage = {
  id: string;
  runId: string;
  url: string;
  status: PartnerDirectoryCrawlPageStatus;
  attempts: number;
  discoveredProfileCount: number;
  nextPageUrl: string | null;
  errorMessage: string | null;
  errorDetails: Record<string, unknown> | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PartnerDirectoryDiscoveredProfile = {
  id: string;
  runId: string;
  sourceSlug: string | null;
  sourceUrl: string;
  firstSeenPageUrl: string;
  lastSeenPageUrl: string;
  status: PartnerDirectoryDiscoveredProfileStatus;
  scrapeTaskId: string | null;
  recordId: string | null;
  duplicateOfId: string | null;
  createdAt: string;
  updatedAt: string;
};

type CreateTaskResponse = {
  taskId: string;
  status: PartnerDirectoryScrapeTaskStatus;
  resultRecordId?: string;
  reusedExisting: boolean;
};

export type PartnerDirectoryApiHealth = {
  ok: boolean;
};

export type PartnerDirectoryApiConfig = {
  defaultListLimit: number;
  maxListLimit: number;
  detailErrorPreviewLimit: number;
};

const baseUrl = (
  (import.meta.env.VITE_PARTNER_DIRECTORY_API_BASE_URL as string | undefined) ??
  (import.meta.env.DEV ? "http://localhost:8085" : undefined)
)?.replace(/\/+$/, "");

const partnerDirectoryApi = axios.create({
  baseURL: baseUrl ? `${baseUrl}/api/admin/partner-directory` : undefined,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const partnerDirectoryHealthApi = axios.create({
  baseURL: baseUrl,
  withCredentials: false,
});

partnerDirectoryApi.interceptors.request.use((config) => {
  if (!baseUrl) {
    throw new Error("Missing VITE_PARTNER_DIRECTORY_API_BASE_URL.");
  }

  const authorization = api.defaults.headers.common.Authorization;
  if (authorization) {
    config.headers.Authorization = authorization;
  }

  return config;
});

export async function createPartnerDirectoryScrapeTask(input: {
  input: string;
  force?: boolean;
}): Promise<CreateTaskResponse> {
  const response = await partnerDirectoryApi.post<CreateTaskResponse>("/scrape-tasks", input);
  return response.data;
}

export async function checkPartnerDirectoryApiHealth(): Promise<PartnerDirectoryApiHealth> {
  if (!baseUrl) {
    throw new Error("Missing VITE_PARTNER_DIRECTORY_API_BASE_URL.");
  }

  const response = await partnerDirectoryHealthApi.get<PartnerDirectoryApiHealth>("/health");
  return response.data;
}

export async function getPartnerDirectoryApiConfig(): Promise<PartnerDirectoryApiConfig> {
  if (!baseUrl) {
    throw new Error("Missing VITE_PARTNER_DIRECTORY_API_BASE_URL.");
  }

  const response = await partnerDirectoryHealthApi.get<PartnerDirectoryApiConfig>("/config");
  return response.data;
}

export async function getPartnerDirectoryScrapeTask(id: string): Promise<PartnerDirectoryScrapeTask> {
  const response = await partnerDirectoryApi.get<{ task: PartnerDirectoryScrapeTask }>(`/scrape-tasks/${id}`);
  return response.data.task;
}

export async function listPartnerDirectoryScrapeTasks(input: {
  limit?: number;
  offset?: number;
} = {}): Promise<PartnerDirectoryScrapeTask[]> {
  const response = await partnerDirectoryApi.get<{ tasks: PartnerDirectoryScrapeTask[] }>("/scrape-tasks", {
    params: input,
  });
  return response.data.tasks;
}

export async function recoverPartnerDirectoryScrapeTasks(): Promise<{ requeued: number }> {
  const response = await partnerDirectoryApi.post<{ requeued: number }>("/scrape-tasks/recover");
  return response.data;
}

export async function repairPartnerDirectoryReviewCountMismatches(): Promise<{ matched: number; scheduled: number }> {
  const response = await partnerDirectoryApi.post<{ matched: number; scheduled: number }>("/scrape-tasks/repair-review-count-mismatches");
  return response.data;
}

export async function getPartnerDirectoryRecord(id: string): Promise<{
  record: PartnerDirectoryRecord;
  derivedDefaults: PartnerDirectoryMappedExpertPayload | null;
}> {
  const response = await partnerDirectoryApi.get<{ record: PartnerDirectoryRecord; derivedDefaults?: PartnerDirectoryMappedExpertPayload }>(`/records/${id}`);
  return {
    record: response.data.record,
    // Older API deployments do not derive the og defaults server-side.
    derivedDefaults: response.data.derivedDefaults ?? null,
  };
}

export async function rescrapePartnerDirectoryRecord(id: string): Promise<{ taskId: string; status: PartnerDirectoryScrapeTaskStatus }> {
  const response = await partnerDirectoryApi.post<{ taskId: string; status: PartnerDirectoryScrapeTaskStatus }>(`/records/${id}/rescrape`);
  return response.data;
}

export async function bulkRescrapePartnerDirectoryRecords(recordIds: string[]): Promise<{ scheduled: number; skippedActive: number; missing: number }> {
  const response = await partnerDirectoryApi.post<{ scheduled: number; skippedActive: number; missing: number }>("/records/bulk/rescrape", { recordIds });
  return response.data;
}

export async function bulkUpdatePartnerDirectoryRecordStatus(
  recordIds: string[],
  status: PartnerDirectoryBulkRecordStatus,
): Promise<{ updated: number; missing: number }> {
  const response = await partnerDirectoryApi.post<{ updated: number; missing: number }>("/records/bulk/status", { recordIds, status });
  return response.data;
}

export async function bulkSyncPartnerDirectoryRecords(
  recordIds: string[],
  input: { targetProfileStatus?: PartnerDirectoryExpertProfileStatus } = {},
): Promise<{ job: PartnerDirectoryImportJob; eligible: number; ineligible: number }> {
  const response = await partnerDirectoryApi.post<{ job: PartnerDirectoryImportJob; eligible: number; ineligible: number }>("/records/bulk/sync", {
    recordIds,
    ...input,
  });
  return response.data;
}

export async function revertPartnerDirectoryImportJob(id: string): Promise<{
  job: PartnerDirectoryImportJob;
  result: PartnerDirectoryImportJobRevertResult;
}> {
  const response = await partnerDirectoryApi.post<{ job: PartnerDirectoryImportJob; result: PartnerDirectoryImportJobRevertResult }>(`/import-jobs/${id}/revert`);
  return response.data;
}

export async function listPartnerDirectoryRecords(input: {
  limit?: number;
  offset?: number;
  search?: string;
  status?: PartnerDirectoryRecordStatusFilter;
  countries?: string[];
  minRating?: number;
  minReviews?: number;
  sort?: PartnerDirectoryRecordSort;
} = {}): Promise<{ records: PartnerDirectoryRecord[]; total: number }> {
  const params = {
    ...input,
    countries: input.countries === undefined ? undefined : JSON.stringify(input.countries),
  };
  const response = await partnerDirectoryApi.get<{ records: PartnerDirectoryRecord[]; total?: number }>("/records", {
    params,
  });
  return {
    records: response.data.records,
    // Older API deployments do not return a total; fall back to the page size.
    total: response.data.total ?? response.data.records.length,
  };
}

export async function listPartnerDirectoryRecordCountries(): Promise<{ countries: string[]; hasUnknownCountry: boolean; total: number }> {
  const response = await partnerDirectoryApi.get<{ countries: string[]; hasUnknownCountry: boolean; total: number }>("/records/countries");
  return response.data;
}

export async function updatePartnerDirectoryRecordStatus(id: string, status: string): Promise<PartnerDirectoryRecord> {
  const response = await partnerDirectoryApi.patch<{ record: PartnerDirectoryRecord }>(`/records/${id}/status`, { status });
  return response.data.record;
}

export async function updatePartnerDirectoryRecordMappedPayload(
  id: string,
  mappedPayload: Record<string, unknown> | null,
): Promise<PartnerDirectoryRecord> {
  const response = await partnerDirectoryApi.patch<{ record: PartnerDirectoryRecord }>(`/records/${id}/mapped`, { mappedPayload });
  return response.data.record;
}

export async function createPartnerDirectoryImportJob(input: {
  targetProfileStatus: PartnerDirectoryExpertProfileStatus;
}): Promise<PartnerDirectoryImportJob> {
  const response = await partnerDirectoryApi.post<{ job: PartnerDirectoryImportJob }>("/import-jobs", input);
  return response.data.job;
}

export async function listPartnerDirectoryImportJobs(input: {
  limit?: number;
  offset?: number;
} = {}): Promise<{ jobs: PartnerDirectoryImportJob[]; total: number }> {
  const response = await partnerDirectoryApi.get<{ jobs: PartnerDirectoryImportJob[]; total: number }>("/import-jobs", { params: input });
  return { jobs: response.data.jobs, total: response.data.total };
}

export async function getPartnerDirectoryImportJob(id: string): Promise<PartnerDirectoryImportJob> {
  const response = await partnerDirectoryApi.get<{ job: PartnerDirectoryImportJob }>(`/import-jobs/${id}`);
  return response.data.job;
}

export async function cancelPartnerDirectoryImportJob(id: string): Promise<PartnerDirectoryImportJob> {
  const response = await partnerDirectoryApi.post<{ job: PartnerDirectoryImportJob }>(`/import-jobs/${id}/cancel`);
  return response.data.job;
}

export async function listPartnerDirectoryImportJobRecords(
  id: string,
  input: { limit?: number; offset?: number; outcome?: PartnerDirectoryImportJobRecordOutcome } = {},
): Promise<{ records: PartnerDirectoryImportJobRecord[]; total: number }> {
  const response = await partnerDirectoryApi.get<{ records: PartnerDirectoryImportJobRecord[]; total: number }>(`/import-jobs/${id}/records`, { params: input });
  return { records: response.data.records, total: response.data.total };
}

export async function syncPartnerDirectoryRecord(
  id: string,
  input: { targetProfileStatus?: PartnerDirectoryExpertProfileStatus } = {},
): Promise<PartnerDirectoryRecord> {
  const response = await partnerDirectoryApi.post<{ record: PartnerDirectoryRecord }>(`/records/${id}/sync`, input);
  return response.data.record;
}

export async function revertPartnerDirectoryRecordImport(id: string): Promise<PartnerDirectoryRecord> {
  const response = await partnerDirectoryApi.post<{ record: PartnerDirectoryRecord }>(`/records/${id}/revert-import`);
  return response.data.record;
}

export async function listPartnerDirectoryRecordNotes(recordId: string): Promise<PartnerDirectoryRecordNote[]> {
  const response = await partnerDirectoryApi.get<{ notes: PartnerDirectoryRecordNote[] }>(`/records/${recordId}/notes`);
  return response.data.notes;
}

export async function createPartnerDirectoryRecordNote(recordId: string, body: string): Promise<PartnerDirectoryRecordNote> {
  const response = await partnerDirectoryApi.post<{ note: PartnerDirectoryRecordNote }>(`/records/${recordId}/notes`, { body });
  return response.data.note;
}

export async function deletePartnerDirectoryRecordNote(noteId: string): Promise<void> {
  await partnerDirectoryApi.delete(`/notes/${noteId}`);
}

export async function listPartnerDirectoryGlobalMappings(input: {
  kind?: PartnerDirectoryGlobalMappingKind;
} = {}): Promise<PartnerDirectoryGlobalMapping[]> {
  const response = await partnerDirectoryApi.get<{ mappings: PartnerDirectoryGlobalMapping[] }>("/global-mappings", { params: input });
  return response.data.mappings;
}

export async function upsertPartnerDirectoryGlobalMapping(input: {
  kind: PartnerDirectoryGlobalMappingKind;
  sourceValue: string;
  targetValue: string | null;
  notes?: string | null;
}): Promise<PartnerDirectoryGlobalMapping> {
  const response = await partnerDirectoryApi.put<{ mapping: PartnerDirectoryGlobalMapping }>("/global-mappings", input);
  return response.data.mapping;
}

export async function deletePartnerDirectoryGlobalMapping(id: string): Promise<void> {
  await partnerDirectoryApi.delete(`/global-mappings/${id}`);
}

export async function listPartnerDirectoryServiceSourceValues(): Promise<{ value: string; recordCount: number }[]> {
  const response = await partnerDirectoryApi.get<{ services: { value: string; recordCount: number }[] }>("/global-mappings/source-values");
  return response.data.services;
}

export async function createPartnerDirectoryCrawlRun(input: {
  seedUrl?: string;
  resetRunId?: string | null;
  options?: Partial<PartnerDirectoryCrawlOptions>;
}): Promise<PartnerDirectoryCrawlRun> {
  const response = await partnerDirectoryApi.post<{ run: PartnerDirectoryCrawlRun }>("/crawl-runs", input);
  return response.data.run;
}

export async function listPartnerDirectoryCrawlRuns(input: {
  limit?: number;
  offset?: number;
  status?: PartnerDirectoryCrawlRunStatus;
} = {}): Promise<PartnerDirectoryCrawlRun[]> {
  const response = await partnerDirectoryApi.get<{ runs: PartnerDirectoryCrawlRun[] }>("/crawl-runs", {
    params: input,
  });
  return response.data.runs;
}

export async function getPartnerDirectoryCrawlRun(id: string): Promise<{
  run: PartnerDirectoryCrawlRun;
  failedPages: PartnerDirectoryCrawlPage[];
  failedProfiles: PartnerDirectoryDiscoveredProfile[];
}> {
  const response = await partnerDirectoryApi.get<{
    run: PartnerDirectoryCrawlRun;
    failedPages: PartnerDirectoryCrawlPage[];
    failedProfiles: PartnerDirectoryDiscoveredProfile[];
  }>(`/crawl-runs/${id}`);
  return response.data;
}

export async function pausePartnerDirectoryCrawlRun(id: string): Promise<PartnerDirectoryCrawlRun> {
  const response = await partnerDirectoryApi.post<{ run: PartnerDirectoryCrawlRun }>(`/crawl-runs/${id}/pause`);
  return response.data.run;
}

export async function resumePartnerDirectoryCrawlRun(id: string): Promise<PartnerDirectoryCrawlRun> {
  const response = await partnerDirectoryApi.post<{ run: PartnerDirectoryCrawlRun }>(`/crawl-runs/${id}/resume`);
  return response.data.run;
}

export async function cancelPartnerDirectoryCrawlRun(id: string): Promise<PartnerDirectoryCrawlRun> {
  const response = await partnerDirectoryApi.post<{ run: PartnerDirectoryCrawlRun }>(`/crawl-runs/${id}/cancel`);
  return response.data.run;
}

export async function resetPartnerDirectoryCrawlRun(id: string): Promise<PartnerDirectoryCrawlRun> {
  const response = await partnerDirectoryApi.post<{ run: PartnerDirectoryCrawlRun }>(`/crawl-runs/${id}/reset`);
  return response.data.run;
}

export async function listPartnerDirectoryCrawlPages(input: {
  runId: string;
  limit?: number;
  offset?: number;
  status?: PartnerDirectoryCrawlPageStatus;
}): Promise<PartnerDirectoryCrawlPage[]> {
  const { runId, ...params } = input;
  const response = await partnerDirectoryApi.get<{ pages: PartnerDirectoryCrawlPage[] }>(`/crawl-runs/${runId}/pages`, {
    params,
  });
  return response.data.pages;
}

export async function listPartnerDirectoryDiscoveredProfiles(input: {
  runId: string;
  limit?: number;
  offset?: number;
  status?: PartnerDirectoryDiscoveredProfileStatus;
}): Promise<PartnerDirectoryDiscoveredProfile[]> {
  const { runId, ...params } = input;
  const response = await partnerDirectoryApi.get<{ profiles: PartnerDirectoryDiscoveredProfile[] }>(`/crawl-runs/${runId}/profiles`, {
    params,
  });
  return response.data.profiles;
}

export async function retryFailedPartnerDirectoryCrawlPages(runId: string): Promise<{ retried: number }> {
  const response = await partnerDirectoryApi.post<{ retried: number }>(`/crawl-runs/${runId}/retry-failed-pages`);
  return response.data;
}

export async function retryFailedPartnerDirectoryScrapes(runId: string): Promise<{ retried: number; enqueued: number; skipped: number }> {
  const response = await partnerDirectoryApi.post<{ retried: number; enqueued: number; skipped: number }>(`/crawl-runs/${runId}/retry-failed-scrapes`);
  return response.data;
}

export async function enqueuePartnerDirectoryCrawlScrapeTasks(input: {
  runId: string;
  force?: boolean;
  limit?: number | null;
  profileIds?: string[];
}): Promise<{ enqueued: number; skipped: number }> {
  const { runId, ...body } = input;
  const response = await partnerDirectoryApi.post<{ enqueued: number; skipped: number }>(`/crawl-runs/${runId}/enqueue-scrape-tasks`, body);
  return response.data;
}

export type PartnerDirectoryBadgeListingSyncOutcome =
  | { badgeId: number; status: "synced" }
  | { badgeId: number; status: "failed"; error: { code: string; message: string } }
  | { badgeId: number; status: "skipped"; reason: "in_progress" | "listing_url_changed" | "not_found" };

export async function syncPartnerDirectoryBadgeListings(badgeIds: number[]): Promise<PartnerDirectoryBadgeListingSyncOutcome[]> {
  const response = await partnerDirectoryApi.post<{ outcomes: PartnerDirectoryBadgeListingSyncOutcome[] }>("/badge-listings/sync", { badgeIds });
  return response.data.outcomes;
}
